import { formatUrl } from '@plone/volto/helpers/Api/Api';

import { normalizeAppUrl } from './appUrl';
import { BATCH_SIZE, getLeadImageUrl, normalizeMediaUrl } from './blogUtils';

export { BATCH_SIZE };

const NEWS_CONTENT_TYPES = new Set(['noticia', 'News Item']);

export const getImageFromContent = (text) => {
  if (!text || typeof text !== 'string') {
    return null;
  }

  const imgIndex = text.search('<img');
  if (imgIndex === -1) {
    return null;
  }

  const srcStartIndex = text.substring(imgIndex).search('src=');
  if (srcStartIndex === -1) {
    return null;
  }

  const delimiter = text[imgIndex + srcStartIndex + 4];
  const srcEndIndex = text
    .substring(imgIndex + srcStartIndex + 5)
    .search(delimiter);

  if (srcEndIndex === -1) {
    return null;
  }

  return text.substring(
    imgIndex + srcStartIndex + 5,
    imgIndex + srcStartIndex + 5 + srcEndIndex,
  );
};

export const getNewsImageFallbackUrl = (item) => {
  if (!item?.['@id'] || !NEWS_CONTENT_TYPES.has(item?.['@type'])) {
    return '';
  }
  return normalizeMediaUrl(`${normalizeAppUrl(item['@id'])}/@@images/image`);
};

export const getNewsImageUrl = (item, scale = 'preview') => {
  const leadUrl = getLeadImageUrl(item, scale);
  if (leadUrl) {
    return leadUrl;
  }

  const contentImage = getImageFromContent(item?.text?.data);
  if (contentImage) {
    return contentImage;
  }

  return getNewsImageFallbackUrl(item);
};

export const getNewsImageStyle = (item) => {
  const imageUrl = getNewsImageUrl(item, 'preview');
  if (imageUrl) {
    return { backgroundImage: `url("${imageUrl}")` };
  }

  return undefined;
};

export const computeNewsPaginationPages = (
  itemsTotal,
  batchSize = BATCH_SIZE,
  newestExtraSize = 0,
) => {
  if (!itemsTotal || itemsTotal <= batchSize) {
    return 1;
  }

  let itemsTotalTemp = itemsTotal - batchSize - newestExtraSize;
  let paginationPages = 1;

  if (itemsTotalTemp > 0) {
    paginationPages += Math.trunc(itemsTotalTemp / batchSize);
    const remainder = itemsTotal % batchSize;
    if (remainder) {
      paginationPages += 1;
    }
  }

  return paginationPages;
};

export const getNewsPageParams = (
  page,
  batchSize = BATCH_SIZE,
  newestExtraSize = 0,
) => {
  let batchStart = (page - 1) * batchSize;
  let batchSizeParam = batchSize;

  if (page > 1) {
    batchStart -= newestExtraSize;
  } else {
    batchSizeParam -= newestExtraSize;
  }

  return { batchStart, batchSize: batchSizeParam };
};

export const getNewsPageUrl = (
  contentId,
  page,
  batchSize = BATCH_SIZE,
  newestExtraSize = 0,
) => {
  const { batchStart, batchSize: batchSizeParam } = getNewsPageParams(
    page,
    batchSize,
    newestExtraSize,
  );
  const path = `${normalizeAppUrl(contentId)}?fullobjects&b_start=${batchStart}&b_size=${batchSizeParam}`;
  return formatUrl(path);
};

export const computeFakeItemsLastRow = ({
  itemsLength,
  batchSize,
  newestExtraSize,
  isFirstPage,
  isLastPage,
  rowSize = 0,
}) => {
  if (!isLastPage) {
    return 0;
  }

  let fakeItems = batchSize - itemsLength;

  if (isFirstPage) {
    fakeItems -= batchSize - newestExtraSize - itemsLength;
  }

  if (rowSize > 0) {
    fakeItems %= rowSize;
  }

  return Math.max(0, fakeItems);
};

export const getShareUrl = (itemId) => {
  if (typeof window === 'undefined') {
    return itemId;
  }
  return `${window.location.protocol}//${window.location.host}${normalizeAppUrl(itemId)}`;
};
